import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { isAxiosError } from 'axios';
import { resetPassword } from '../services/auth.service';
import { extractServerErrors } from '@/lib/api/error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'At least 8 characters.')
      .regex(/[A-Za-z]/, 'Must contain a letter.')
      .regex(/[0-9]/, 'Must contain a digit.'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match.',
  });

type FormValues = z.infer<typeof schema>;

export function ResetPasswordForm() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [tokenError, setTokenError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) setTokenError('This reset link is missing a token.');
  }, [token]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const { isSubmitting } = form.formState;

  async function onSubmit(values: FormValues) {
    try {
      await resetPassword(token, values.password);
      toast.success('Password updated. Please sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        const msgs = extractServerErrors(err);
        setTokenError(msgs[0] ?? 'This reset link is invalid or has already been used.');
      } else {
        toast.error('Network error. Please check your connection.');
      }
    }
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-muted/30 px-4 pb-4 pt-20 sm:pt-28">
      <Card className="w-full max-w-sm border-border/50 shadow-xl">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#22c55e] shadow-sm">
              <span className="text-lg font-bold text-white">AV</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Set a new password</h1>
            <p className="text-sm text-muted-foreground">This reset link works once.</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {tokenError && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{tokenError}</span>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={isSubmitting || !token}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Saving…' : 'Save new password'}
              </Button>
            </form>
          </Form>

          <p className="text-center text-xs text-muted-foreground">
            Passwords must be at least 8 characters and include a letter and a digit.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
